import React, { useState } from "react";

const anchor = (n, side) => {
  switch (side) {
    case "t":
      return [n.x + n.w / 2, n.y];
    case "b":
      return [n.x + n.w / 2, n.y + n.h];
    case "l":
      return [n.x, n.y + n.h / 2];
    default:
      return [n.x + n.w, n.y + n.h / 2];
  }
};

// Orthogonal path between two anchors.
const route = ([x1, y1], fs, [x2, y2], ts) => {
  if (fs === "r" || fs === "l") {
    if (y1 === y2) return `M${x1} ${y1} H${x2}`;
    const mx = (x1 + x2) / 2;
    return `M${x1} ${y1} H${mx} V${y2} H${x2}`;
  }
  if (x1 === x2) return `M${x1} ${y1} V${y2}`;
  const my = (y1 + y2) / 2;
  return `M${x1} ${y1} V${my} H${x2} V${y2}`;
};

function Diagram({ diagram, title }) {
  const [active, setActive] = useState(null);
  const byId = Object.fromEntries(diagram.nodes.map((n) => [n.id, n]));
  const activeNode = active ? byId[active] : null;

  const edgeActive = (e) => active && (e.from === active || e.to === active);

  return (
    <div className="dg">
      <div className="dg-scroll">
        <svg
          viewBox={`0 0 ${diagram.width} ${diagram.height}`}
          style={{ minWidth: Math.min(diagram.width, 620) }}
          role="img"
          aria-label={`Architecture diagram: ${title}`}
        >
          <defs>
            <marker
              id="dg-arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto"
            >
              <path className="dg-head" d="M2 1L8 5L2 9" />
            </marker>
          </defs>

          {(diagram.labels || []).map((l) => (
            <text key={l.text} x={l.x} y={l.y} className="dg-track">
              {l.text}
            </text>
          ))}

          {diagram.edges.map((e, i) => {
            const a = byId[e.from];
            const b = byId[e.to];
            const p1 = anchor(a, e.fs);
            const p2 = anchor(b, e.ts);
            const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
            return (
              <g key={i}>
                <path
                  d={route(p1, e.fs, p2, e.ts)}
                  className={
                    "dg-edge" +
                    (e.dashed ? " dashed" : "") +
                    (edgeActive(e) ? " on" : "")
                  }
                  markerEnd="url(#dg-arrow)"
                />
                {e.label && (
                  <text x={mid[0]} y={mid[1] - 6} className="dg-elabel">
                    {e.label}
                  </text>
                )}
              </g>
            );
          })}

          {diagram.nodes.map((n, i) => (
            <g
              key={n.id}
              style={{ "--i": i }}
              className={
                "dg-node " + n.kind + (active === n.id ? " on" : "")
              }
              tabIndex={0}
              onMouseEnter={() => setActive(n.id)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(n.id)}
              onBlur={() => setActive(null)}
            >
              <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="8" />
              <text x={n.x + n.w / 2} y={n.y + n.h / 2 - 3} className="dg-title">
                {n.label}
              </text>
              <text x={n.x + n.w / 2} y={n.y + n.h / 2 + 13} className="dg-sub">
                {n.sub}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <div className="dg-note" aria-live="polite">
        {activeNode ? (
          <>
            <b>{activeNode.label}.</b> {activeNode.note}
          </>
        ) : (
          <span className="muted">Hover a box to see what it does.</span>
        )}
      </div>
    </div>
  );
}

export default Diagram;
